import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemberList } from '../../../hooks/useMember.js';
import { ChildTicketKindPicker } from './ChildTicketKindPicker.js';
import { ChildTicketSheetCell, type CellOptions } from './ChildTicketSheetCell.js';
import {
  CHILD_TICKET_ITEMS,
  CHILD_TICKET_REPOSITORIES,
  toPickerOptions,
} from './child-ticket-options.js';
import {
  autoPointRowIds,
  resolveChildTicketPoints,
} from '@cortex/domain-product-innovation-lab-basic-design-tree';
import {
  SHEET_COLUMNS,
  applyPaste,
  clearRange,
  isInRange,
  isReadOnlyCell,
  moveCell,
  normalizeRange,
  parseTsv,
  rangeToTsv,
  writeCell,
  type SheetCell,
  type SheetColumn,
  type SheetOptions,
} from './child-ticket-sheet.js';
import type { ChildTicketRow } from '@cortex/domain-product-innovation-lab-basic-design-tree';

/** 行の編集ハンドラ一式。 */
export interface ChildTicketPlanTableProps {
  /** 表示する行（この順で描く）。 */
  rows: readonly ChildTicketRow[];
  /** 行の並びごと差し替える（貼り付け・消去がまとめて書くため）。 */
  onReplaceRows: (rows: ChildTicketRow[]) => void;
  /** 1 行の 1 列を書き換える。 */
  onChangeRow: (rowId: string, patch: Partial<ChildTicketRow>) => void;
  /** 行を複製する（元の行の直後に入る）。 */
  onDuplicateRow: (rowId: string) => void;
  /** 行を削除する。 */
  onRemoveRow: (rowId: string) => void;
  /** 行を並べ替える（`from` 番目を `to` 番目へ）。 */
  onMoveRow: (from: number, to: number) => void;
  /** `index` の位置へ行を差し込む。 */
  onInsertRow: (index: number) => void;
}

/** 列ごとの見出し翻訳キー。表の列順と `SHEET_COLUMNS` を一致させる。 @graph-connects none */
const COLUMN_LABEL_KEY: Readonly<Record<SheetColumn, string>> = {
  kind: 'childTicketPlan.columns.kind',
  item: 'childTicketPlan.columns.item',
  repository: 'childTicketPlan.columns.repository',
  content: 'childTicketPlan.columns.content',
  point: 'childTicketPlan.columns.point',
  assignee: 'childTicketPlan.columns.assignee',
};

/**
 * セルを選んでいる間、ページのスクロールではなく表の移動に使うキー。
 * @graph-connects none
 */
const NAV_KEYS: readonly string[] = [
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
];

/** 1 文字の印字可能キーか（打ち始めで編集に入るかの判定）。 @graph-connects none */
function isTypingKey(e: React.KeyboardEvent): boolean {
  return e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 基本設計シートの編集表。種別以外の列は表計算と同じく矢印キーで移動し、範囲選択・コピー・貼り付け・消去ができる。pt は自動計算の種別だけ読み取り専用で計算値を出す
 * @graph-connects ChildTicketKindPicker [calls] 種別セルのメニュー
 * @graph-connects ChildTicketSheetCell [calls] セル 1 つの表示と編集
 * @graph-connects resolveChildTicketPoints [calls] 行ごとの確定 pt
 * @graph-connects useMemberList [calls] 担当者候補（nickname）の取得
 */
export function ChildTicketPlanTable({
  rows,
  onReplaceRows,
  onChangeRow,
  onDuplicateRow,
  onRemoveRow,
  onMoveRow,
  onInsertRow,
}: ChildTicketPlanTableProps): ReactNode {
  const { t } = useTranslation();
  const memberListQuery = useMemberList({ organization: 'all' });
  const assigneeNames = useMemo(() => {
    const data = memberListQuery.data;
    if (data === undefined) return [];
    const nicknames = new Set<string>();
    for (const member of [...data.engineers, ...data.qa]) {
      const nickname = member.nickName ?? member.name;
      if (nickname !== '') nicknames.add(nickname);
    }
    return [...nicknames].sort((a, b) => a.localeCompare(b));
  }, [memberListQuery.data]);

  const cellOptions: CellOptions = useMemo(
    () => ({
      item: toPickerOptions(CHILD_TICKET_ITEMS),
      repository: toPickerOptions(CHILD_TICKET_REPOSITORIES),
      assignee: toPickerOptions(assigneeNames),
    }),
    [assigneeNames],
  );
  // pt が自動で決まる行（同じ種別の 1 行目）。編集可否も行の塗りもここから決める。
  const autoIds = useMemo(() => autoPointRowIds(rows), [rows]);
  const sheetOptions: SheetOptions = useMemo(
    () => ({
      items: CHILD_TICKET_ITEMS,
      repositories: CHILD_TICKET_REPOSITORIES,
      assignees: assigneeNames,
      autoPointRowIds: autoIds,
    }),
    [assigneeNames, autoIds],
  );
  const resolvedPoints = useMemo(() => resolveChildTicketPoints(rows), [rows]);

  // 表計算の操作状態。focus = 今いるセル、anchor = 範囲の起点、editing = 編集中のセル。
  const [focus, setFocus] = useState<SheetCell | null>(null);
  const [anchor, setAnchor] = useState<SheetCell | null>(null);
  const [editing, setEditing] = useState<SheetCell | null>(null);
  const [seed, setSeed] = useState<string | null>(null);
  const cellRefs = useRef(new Map<string, HTMLTableCellElement>());
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());
  const tableRef = useRef<HTMLTableElement | null>(null);
  // 掴んでいる行と、今その手前に落とそうとしている行
  const [dragRow, setDragRow] = useState<number | null>(null);
  const [dropRow, setDropRow] = useState<number | null>(null);
  const range = focus !== null && anchor !== null ? normalizeRange(anchor, focus) : null;

  // 編集していないときは、今いるセルに実フォーカスを戻す（キー入力を表で受けるため）。
  useEffect(() => {
    if (focus === null || editing !== null) return;
    cellRefs.current.get(`${String(focus.row)}:${String(focus.col)}`)?.focus();
  }, [focus, editing]);

  const select = useCallback((cell: SheetCell, extend: boolean): void => {
    setFocus(cell);
    if (!extend) setAnchor(cell);
  }, []);

  // セルを選んでいる間、方向キーは**常に**表の移動にする。
  //
  // 下の tbody のハンドラはセル自身にフォーカスが乗っているときだけ動くので、
  // 候補リストを閉じた直後など実フォーカスが body に落ちた瞬間があると、同じキーが
  // ページのスクロールに化ける。ここで document でも受けて取りこぼしを無くす。
  // tbody 側で処理済みなら defaultPrevented で分かるため二重に動かない。
  useEffect(() => {
    if (focus === null || editing !== null) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.defaultPrevented || !NAV_KEYS.includes(e.key)) return;
      const el = e.target instanceof Element ? e.target : null;
      // 入力中の文字送りと、重ねて出しているメニューの操作は奪わない
      if (
        el?.closest(
          'input, textarea, select, [contenteditable="true"], [data-child-ticket-overlay]',
        ) != null
      ) {
        return;
      }
      e.preventDefault();
      select(moveCell(focus, e.key, rows.length), e.shiftKey);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [focus, editing, rows.length, select]);

  // 表の外を押したらセル選択を解く。選んだままだと、別の場所に居るのに方向キーを
  // 表が取り続けてページが動かなくなる。重ねて出している候補リストは外側に数えない。
  useEffect(() => {
    if (focus === null) return;
    const onPointerDown = (e: PointerEvent): void => {
      const el = e.target instanceof Element ? e.target : null;
      if (el === null) return;
      if (tableRef.current?.contains(el) === true) return;
      if (el.closest('[data-child-ticket-overlay]') != null) return;
      setFocus(null);
      setAnchor(null);
      setEditing(null);
      setSeed(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [focus]);

  const commitCell = useCallback(
    (cell: SheetCell, raw: string): void => {
      const row = rows[cell.row];
      const column = SHEET_COLUMNS[cell.col];
      if (row === undefined || column === undefined) return;
      const written = writeCell(row, column, raw, sheetOptions);
      if (written !== row) {
        onReplaceRows(rows.map((r) => (r.rowId === row.rowId ? written : r)));
      }
      setEditing(null);
      setSeed(null);
    },
    [rows, sheetOptions, onReplaceRows],
  );

  const onGridKeyDown = (e: React.KeyboardEvent<HTMLTableSectionElement>): void => {
    if (focus === null || editing !== null) return;
    const key = e.key;
    if (NAV_KEYS.includes(key)) {
      e.preventDefault();
      select(moveCell(focus, key, rows.length), e.shiftKey);
      return;
    }
    if (key === 'Tab') {
      e.preventDefault();
      select(moveCell(focus, e.shiftKey ? 'ArrowLeft' : 'ArrowRight', rows.length), false);
      return;
    }
    if (key === 'Enter' || key === 'F2') {
      e.preventDefault();
      setEditing(focus);
      return;
    }
    if (key === 'Escape') {
      setAnchor(focus);
      return;
    }
    if ((key === 'Delete' || key === 'Backspace') && range !== null) {
      e.preventDefault();
      onReplaceRows(clearRange(rows, range, sheetOptions));
      return;
    }
    if (isTypingKey(e)) {
      const row = rows[focus.row];
      const column = SHEET_COLUMNS[focus.col];
      if (row === undefined || column === undefined || isReadOnlyCell(row, column, sheetOptions))
        return;
      e.preventDefault();
      setSeed(column === 'content' || column === 'point' ? key : null);
      setEditing(focus);
    }
  };

  return (
    <div className="overflow-x-auto rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <table
        ref={tableRef}
        role="grid"
        aria-label={t('childTicketPlan.heading')}
        className="w-full min-w-[1180px] table-fixed border-collapse text-sm"
      >
        <colgroup>
          <col className="w-[64px]" />
          <col className="w-[268px]" />
          <col className="w-[152px]" />
          <col className="w-[216px]" />
          <col />
          <col className="w-[92px]" />
          <col className="w-[208px]" />
          <col className="w-[84px]" />
        </colgroup>
        <thead>
          <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
            <th className="px-3 py-3 text-left text-[11px] font-semibold text-[var(--color-text-muted)]">
              <span className="sr-only">{t('childTicketPlan.columns.order')}</span>
            </th>
            {SHEET_COLUMNS.map((column) => (
              <th
                key={column}
                className={`px-3 py-3 text-[11px] font-semibold text-[var(--color-text-muted)] ${
                  column === 'point' ? 'text-right' : 'text-left'
                }`}
              >
                {t(COLUMN_LABEL_KEY[column])}
              </th>
            ))}
            <th className="px-3 py-3 text-right text-[11px] font-semibold text-[var(--color-text-muted)]">
              <span className="sr-only">{t('childTicketPlan.columns.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody
          onKeyDown={onGridKeyDown}
          onCopy={(e) => {
            if (range === null || editing !== null) return;
            e.preventDefault();
            e.clipboardData.setData('text/plain', rangeToTsv(rows, range));
          }}
          onCut={(e) => {
            if (range === null || editing !== null) return;
            e.preventDefault();
            e.clipboardData.setData('text/plain', rangeToTsv(rows, range));
            onReplaceRows(clearRange(rows, range, sheetOptions));
          }}
          onPaste={(e) => {
            if (range === null || editing !== null) return;
            e.preventDefault();
            const matrix = parseTsv(e.clipboardData.getData('text/plain'));
            onReplaceRows(applyPaste(rows, range, matrix, sheetOptions));
          }}
        >
          {rows.map((row, index) => {
            const rowLabel = String(index + 1);
            return (
              <tr
                key={row.rowId}
                ref={(el) => {
                  if (el === null) rowRefs.current.delete(row.rowId);
                  else rowRefs.current.set(row.rowId, el);
                }}
                // pt を自動計算する行は地をわずかに灰へ寄せる（pt 欄に打つ必要が
                // 無いことを、説明文を増やさずに見た目だけで伝える）
                data-auto-point={autoIds.has(row.rowId)}
                data-dragging={dragRow === index}
                data-drop-target={dropRow === index && dragRow !== index}
                onDragOver={(e) => {
                  if (dragRow === null) return;
                  // preventDefault しないと drop が起きない（HTML のドラッグの決まり）
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDropRow(index);
                }}
                onDrop={(e) => {
                  if (dragRow === null) return;
                  e.preventDefault();
                  if (dragRow !== index) onMoveRow(dragRow, index);
                  setDragRow(null);
                  setDropRow(null);
                }}
                className="child-ticket-row group/row border-b border-[var(--color-border)] last:border-b-0"
              >
                <td className="relative px-3 py-2 align-middle">
                  <button
                    type="button"
                    aria-label={t('childTicketPlan.row.insertAbove', { row: rowLabel })}
                    title={t('childTicketPlan.row.insertAbove', { row: rowLabel })}
                    tabIndex={-1}
                    onClick={() => {
                      onInsertRow(index);
                    }}
                    className="child-ticket-action absolute -top-3 left-2 z-10 grid h-6 w-6 cursor-pointer place-items-center rounded-full border border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-xs text-[var(--color-text-muted)] opacity-0 transition-opacity hover:border-[var(--color-accent-primary)] hover:text-[var(--color-accent-primary)] group-hover/row:opacity-100"
                  >
                    <span aria-hidden="true">＋</span>
                  </button>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      aria-label={t('childTicketPlan.row.drag', { row: rowLabel })}
                      tabIndex={-1}
                      // 掴めるのは取っ手だけ。行そのものを draggable にすると、
                      // セルの範囲選択（Shift + ドラッグ）が掴み上げに化ける
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', rowLabel);
                        // 取っ手だけを掴んだ絵にすると何を動かしているか分からないので、
                        // 行そのものをドラッグ中の見た目に使う
                        const el = rowRefs.current.get(row.rowId);
                        if (el != null) e.dataTransfer.setDragImage(el, 24, 16);
                        setDragRow(index);
                      }}
                      onDragEnd={() => {
                        setDragRow(null);
                        setDropRow(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
                        // 取っ手に居る間の上下は行の移動。表のセル移動へは渡さない
                        e.preventDefault();
                        e.stopPropagation();
                        onMoveRow(index, index + (e.key === 'ArrowDown' ? 1 : -1));
                      }}
                      className="grid h-7 w-5 cursor-grab place-items-center rounded-lg text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-secondary)] active:cursor-grabbing"
                    >
                      <span aria-hidden="true">⠿</span>
                    </button>
                    <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
                      {rowLabel}
                    </span>
                  </div>
                </td>
                {SHEET_COLUMNS.map((column, col) => {
                  const cell: SheetCell = { row: index, col };
                  const isFocused = focus?.row === index && focus.col === col;
                  const isSelected = range !== null && isInRange(range, index, col);
                  const isEditing = editing?.row === index && editing.col === col;
                  const readOnly = isReadOnlyCell(row, column, sheetOptions);
                  const ariaLabel = t(`childTicketPlan.row.${column}`, { row: rowLabel });
                  return (
                    <td
                      key={column}
                      role="gridcell"
                      aria-selected={isSelected}
                      aria-readonly={readOnly}
                      aria-label={ariaLabel}
                      tabIndex={isFocused ? 0 : -1}
                      ref={(el) => {
                        const key = `${String(index)}:${String(col)}`;
                        if (el === null) cellRefs.current.delete(key);
                        else cellRefs.current.set(key, el);
                      }}
                      data-focused={isFocused}
                      data-selected={isSelected}
                      data-readonly={readOnly}
                      onMouseDown={(e) => {
                        if (isEditing) return;
                        select(cell, e.shiftKey);
                      }}
                      onDoubleClick={() => {
                        if (!readOnly) setEditing(cell);
                      }}
                      className={`child-sheet-cell px-3 py-2 ${
                        column === 'kind' ? 'align-middle' : 'align-top'
                      }`}
                    >
                      {column === 'kind' ? (
                        <ChildTicketKindPicker
                          value={row.kindId}
                          rowLabel={rowLabel}
                          open={isEditing}
                          onOpenChange={(next) => {
                            setEditing(next ? cell : null);
                            setSeed(null);
                          }}
                          onChange={(kindId) => {
                            onChangeRow(row.rowId, { kindId });
                          }}
                        />
                      ) : (
                        <ChildTicketSheetCell
                          row={row}
                          column={column}
                          editing={isEditing}
                          seed={isEditing ? seed : null}
                          readOnlyValue={
                            readOnly ? String(resolvedPoints.get(row.rowId) ?? 0) : null
                          }
                          options={cellOptions}
                          ariaLabel={ariaLabel}
                          onCommit={(next) => {
                            commitCell(cell, next);
                          }}
                          onCancel={() => {
                            setEditing(null);
                            setSeed(null);
                          }}
                        />
                      )}
                    </td>
                  );
                })}
                <td className="px-3 py-2 align-middle">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      type="button"
                      aria-label={t('childTicketPlan.row.duplicate', { row: rowLabel })}
                      title={t('childTicketPlan.row.duplicate', { row: rowLabel })}
                      tabIndex={-1}
                      onClick={() => {
                        onDuplicateRow(row.rowId);
                      }}
                      className="child-ticket-action grid h-7 w-7 cursor-pointer place-items-center rounded-full border border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-accent-primary)] hover:text-[var(--color-accent-primary)]"
                    >
                      <span aria-hidden="true" className="text-xs">
                        ⧉
                      </span>
                    </button>
                    <button
                      type="button"
                      aria-label={t('childTicketPlan.row.remove', { row: rowLabel })}
                      title={t('childTicketPlan.row.remove', { row: rowLabel })}
                      tabIndex={-1}
                      onClick={() => {
                        onRemoveRow(row.rowId);
                      }}
                      className="child-ticket-action grid h-7 w-7 cursor-pointer place-items-center rounded-full border border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-error)] hover:text-[var(--color-error)]"
                    >
                      <span aria-hidden="true" className="text-xs">
                        ✕
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t border-[var(--color-border)]">
            <td colSpan={SHEET_COLUMNS.length + 2} className="px-3 py-2">
              <button
                type="button"
                onClick={() => {
                  onInsertRow(rows.length);
                }}
                className="flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-dashed border-[var(--color-border)] py-2 text-xs font-medium text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-accent-primary)] hover:text-[var(--color-accent-primary)]"
              >
                <span aria-hidden="true">＋</span>
                {t('childTicketPlan.actions.addRow')}
              </button>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
